import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import "./styles.css";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Defaults sections
import { Pack } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Custom components
import PackItem from "./PackItem";

// MUI
import { Collapse, Grid, Box } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

export default function PackSection(props) {
	const {
		identifier = "",
		packs = [],
		emptyError = {
			error: false,
			emptyMessage: "Please add one or more model-based commissions.",
		},
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const packsErrors = errors?.get("packs");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onPacksUpdate = (updatedPacks) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(updatedPacks);
		}
	};

	const [innerPacks, setPacks] = useCallbackState([], onPacksUpdate);

	const addPack = () => {
		const updated = [...innerPacks];
		updated.push(Pack());
		setPacks(updated);
	};

	const updatePack = (index, pack) => {
		const updated = [...innerPacks];
		updated[index] = pack;
		savePacks(updated);
	};

	const removePack = (index) => {
		if (index > -1) {
			const updated = [...innerPacks];
			updated.splice(index, 1);
			savePacks(updated);
		}
	};

	const savePacks = (updatedPacks) => {
		wasUpdated.current = true;
		setPacks(updatedPacks);
	};

	useMemo(() => {
		validate([
			{
				definition: {
					useKey: "packs",
					operators: {
						conditions: [
							{ type: "SELF", on: "title", useModifier: { function: "emptyTitle" }, logic: "not" },
							{ type: "SELF", on: "amount.value", useModifier: { function: "emptyAmount" }, logic: "not" },
						],
						modifiers: {
							emptyTitle: (value) => !value,
							emptyAmount: (value) => !value,
						},
					},
					errorsMap: ["title", "amount"],
					message: "Invalid pack definition",
					validationType: "MULTIPLE",
				},
				section: innerPacks || [],
			},
		]);
	}, [innerPacks, validate]);

	useEffect(() => {
		setPacks(clone(packs));

		return () => {
			setPacks([]);
			wasUpdated.current = false;
		};
	}, [packs, setPacks]);

	return (
		<Box className="draw-frame">
			<Box className="pack-inner">
				<Grid container direction="column" sx={{ flexWrap: "nowrap" }}>
					<Grid item mt={2} sx={{ marginRight: "40px" }}>
						<Grid container direction="row" justifyContent="flex-start">
							<Grid item xs={12}>
								<Grid container direction="column" alignItems="flex-start">
									<Grid item container direction="column" rowSpacing={0.5}>
										<TransitionGroup>
											{innerPacks.map((pack, key) => (
												<Collapse key={`collapse_pack_${identifier}_${key}`} component={Grid} item container>
													<PackItem
														index={key}
														pack={pack}
														errors={packsErrors?.[key]}
														readOnly={readOnly}
														onActionDelete={removePack}
														onActionUpdate={updatePack}
													/>
												</Collapse>
											))}
										</TransitionGroup>
									</Grid>

									<Grid container direction="column" rowSpacing={0.2} alignItems="flex-start">
										{errorsMessages.map((message, key) => (
											<TierAlertError key={`error_pack_${identifier}_${message}_${key}`} text={message} />
										))}
									</Grid>

									{Boolean(emptyError.error && !innerPacks?.length) && (
										<Grid container justifyContent={"center"} mt={1}>
											<TierAlertError text={emptyError.emptyMessage} />
										</Grid>
									)}

									<Grid item container justifyContent={"center"} mt={2.2}>
										<Button
											variant="contained"
											text="Add Pack"
											classes="tier-btn add-tier-btn"
											bgColor={"var(--primary-color)"}
											endIcon={<AddCircleRounded />}
											disabled={!isValid || readOnly}
											onClick={addPack}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}
PackSection.propTypes = {
	identifier: PropTypes.string,
	packs: PropTypes.array,
	emptyError: PropTypes.shape({
		error: PropTypes.bool,
		emptyMessage: PropTypes.string,
	}),
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
